/** LAYOUT — make a sheet, rename one, or delete one.
 *
 * ⚠⚠ **ONE DIALOG FOR THE THREE, BECAUSE THE MODE ALREADY SAYS WHICH.** They ask for at most a
 * name and a paper size, and three files would be three places for the paper table to drift from
 * `plot.py`'s.
 *
 * ⚠ Deleting a layout takes its title block, its notes and its windows with it — the model is
 * untouched, but the composition is not, and that is work. So the delete leads with what is on
 * the sheet rather than with a bare "are you sure".
 */
import { useEffect, useState } from "react";

/** ⚠ The same six ISO sizes as `plot.py`'s `PAGES`, and in millimetres for the same reason. A
 *  second paper table is how A2 comes to be 419 mm wide in one of them. */
const PAGES = ["A4", "A3", "A2", "A1", "A0", "A5"];

export interface LayoutSpec {
  name: string;
  page: string;
  landscape: boolean;
}

export default function LayoutDialog({ mode, current, count, taken, onGo, onClose }: {
  mode: "new" | "rename" | "delete";
  /** The layout being renamed or deleted. */
  current?: string;
  /** How many objects are on the sheet about to be deleted. */
  count?: number;
  /** Every layout name already in the drawing, so a clash is caught before the round trip. */
  taken: string[];
  onGo: (s: LayoutSpec) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(mode === "rename" ? (current || "") : "");
  const [page, setPage] = useState("A3");
  const [land, setLand] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const go = () => {
    if (mode === "delete") return onGo({ name: current || "", page, landscape: land });
    const n = name.trim();
    if (!n) return setErr("A layout needs a name.");
    if (n.toLowerCase() === "model") return setErr("“Model” is model space.");
    // ⚠ Checked here as well as on the server: the server's refusal arrives after this closed.
    if (taken.some((t) => t.toLowerCase() === n.toLowerCase()
                          && t.toLowerCase() !== (current || "").toLowerCase())) {
      return setErr(`This drawing already has a layout called “${n}”.`);
    }
    onGo({ name: n, page, landscape: land });
  };

  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "grid", placeItems: "center", zIndex: 95 }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 400, background: "#3c3f41", border: "1px solid #555",
                    borderRadius: 4, boxShadow: "0 12px 40px rgba(0,0,0,.6)", color: "#e8e8e8" }}>
        <div style={{ padding: "7px 12px", background: "#2f3133", borderBottom: "1px solid #555",
                      font: "600 12px system-ui", borderRadius: "4px 4px 0 0" }}>
          {mode === "new" ? "New Layout"
           : mode === "rename" ? `Rename “${current}”`
           : `Delete “${current}”`}
        </div>
        <div style={{ padding: 12, display: "grid", gap: 10, font: "11.5px system-ui" }}>
          {mode === "delete" ? (
            <div style={{ color: "#c8a35a", lineHeight: 1.6 }}>
              This sheet and the {count ?? 0} object{count === 1 ? "" : "s"} on it —
              its title block, notes and viewports — go together.
              <div style={{ color: "#9aa0a6", marginTop: 6 }}>
                The model is untouched, and UNDO brings the whole sheet back.
              </div>
            </div>
          ) : (
            <label style={{ display: "grid", gap: 3 }}>
              <span style={{ color: "#9aa0a6" }}>Name</span>
              <input autoFocus value={name} data-testid="layout-name"
                     onChange={(e) => { setName(e.target.value); setErr(null); }}
                     onKeyDown={(e) => { if (e.key === "Enter") go(); }} style={inp} />
            </label>
          )}

          {mode === "new" && (
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <label style={{ display: "grid", gap: 3, flex: 1 }}>
                <span style={{ color: "#9aa0a6" }}>Paper</span>
                <select value={page} onChange={(e) => setPage(e.target.value)}
                        data-testid="layout-page" style={inp}>
                  {PAGES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, flex: 1,
                              paddingBottom: 5 }}>
                <input type="checkbox" checked={land} data-testid="layout-landscape"
                       onChange={(e) => setLand(e.target.checked)} />
                Landscape
              </label>
            </div>
          )}

          {err && <div data-testid="layout-error" style={{ color: "#ff8f8f" }}>{err}</div>}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button style={btn} onClick={onClose} data-testid="layout-cancel">Cancel</button>
            <button style={{ ...btn,
                             background: mode === "delete" ? "#7a3b3b" : "#3c5a82",
                             borderColor: mode === "delete" ? "#9c4a4a" : "#4a6d9c" }}
                    data-testid="layout-ok" onClick={go}>
              {mode === "new" ? "Create" : mode === "rename" ? "Rename" : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inp: React.CSSProperties = {
  background: "#2b2d2f", border: "1px solid #555", color: "#e8e8e8",
  padding: "4px 7px", borderRadius: 2, font: "12px system-ui",
};
const btn: React.CSSProperties = {
  background: "#4a4d4f", border: "1px solid #5d6164", color: "#e8e8e8",
  padding: "4px 14px", borderRadius: 2, cursor: "pointer", font: "12px system-ui",
};
